<template>
  <v-dialog v-model="openModal" persistent max-width="860px">
    <v-card class="setup-log-dialog">
      <!-- Header -->
      <div class="modal-header">
        <div class="modal-header__left">
          <div class="modal-header__icon-wrap">
            <v-icon class="modal-header__icon">terminal</v-icon>
          </div>
          <div>
            <div class="modal-header__title">Server Setup Logs</div>
            <div class="modal-header__sub" v-if="company && company.company_no">
              {{ company.company_no }}
            </div>
          </div>
        </div>
        <v-btn icon flat class="modal-close-btn" @click="openModal = false">
          <v-icon>close</v-icon>
        </v-btn>
      </div>

      <v-divider class="modal-divider"></v-divider>

      <!-- Log type tabs -->
      <div class="log-tabs">
        <button
          class="log-tab"
          :class="{ 'log-tab--active log-tab--info': mode === 'info' }"
          @click="mode = 'info'"
        >
          <span class="log-tab__dot log-tab__dot--info"></span>
          INFO
          <span class="log-tab__count">{{ response['info'].length }}</span>
        </button>
        <button
          class="log-tab"
          :class="{ 'log-tab--active log-tab--cmd': mode === 'cmd' }"
          @click="mode = 'cmd'"
        >
          <span class="log-tab__dot log-tab__dot--cmd"></span>
          CMD
          <span class="log-tab__count">{{ response['cmd'].length }}</span>
        </button>
        <button
          class="log-tab"
          :class="{ 'log-tab--active log-tab--error': mode === 'error' }"
          @click="mode = 'error'"
        >
          <span class="log-tab__dot log-tab__dot--error"></span>
          ERROR
          <span class="log-tab__count">{{ response['error'].length }}</span>
        </button>
      </div>

      <!-- Log output -->
      <div class="log-output">
        <template v-if="hasLogs">
          <div
            v-if="mode === 'info'"
            v-for="(res, idx) in response['info']"
            :key="'info-' + idx"
            class="log-line log-line--info"
          >
            <span class="log-line__prefix">INFO</span>
            <span class="log-line__text">{{ res }}</span>
          </div>
          <div
            v-if="mode === 'error'"
            v-for="(res, idx) in response['error']"
            :key="'err-' + idx"
            class="log-line log-line--error"
          >
            <span class="log-line__prefix">ERR</span>
            <span class="log-line__text">{{ res }}</span>
          </div>
          <div
            v-if="mode === 'cmd'"
            v-for="(res, idx) in response['cmd']"
            :key="'cmd-' + idx"
            class="log-line log-line--cmd"
          >
            <span class="log-line__prefix">$</span>
            <span class="log-line__text">{{ res }}</span>
          </div>
        </template>
        <div v-else class="log-empty">
          <v-icon class="log-empty__icon">article</v-icon>
          <p class="log-empty__text">No {{ mode }} logs yet. Click <strong>Start</strong> to begin.</p>
        </div>
      </div>

      <v-divider class="modal-divider"></v-divider>

      <!-- Footer with timer + action -->
      <div class="modal-footer">
        <div class="log-timer" v-if="timer">
          <v-icon small class="log-timer__icon">timer</v-icon>
          <span class="log-timer__text">Running {{ parseInt(offset) }}s</span>
        </div>
        <v-spacer></v-spacer>
        <v-btn flat class="modal-cancel-btn" @click="openModal = false" :disabled="!!timer">
          Close
        </v-btn>
        <v-btn
          depressed
          :color="button === 'Close' ? 'success' : 'primary'"
          class="modal-action-btn"
          @click="setupServer"
          :disabled="!!timer && button !== 'Close'"
        >
          <v-icon left small>{{ button === 'Close' ? 'check_circle' : 'play_arrow' }}</v-icon>
          {{ timer ? `${parseInt(offset)}s…` : button }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import API from "@/mixins/APIServer";
import APICompany from "@/mixins/APICompany";

export default {
  props: {
    company: {
      type: Object,
      default: () => ({})
    }
  },
  mixins: [API, APICompany],
  data() {
    return {
      mode: "info",
      openModal: false,
      time: null,
      offset: 0,
      timer: null,
      button: "Start",
      response: { info: [], error: [], cmd: [] }
    };
  },
  computed: {
    hasLogs() {
      return (
        this.response[this.mode] && this.response[this.mode].length > 0
      );
    }
  },
  methods: {
    showDialog: function() {
      this.openModal = true;
      this.response = { info: [], error: [], cmd: [] };
      this.button = "Start";
      this.mode = "info";
      this.time = null;
      this.offset = 0;
      this.timer = null;
    },
    setupServer: async function() {
      if (this.button === "Close") {
        this.openModal = false;
        return;
      }
      if (this.time) return;

      const { company_no } = this.company;
      if (!company_no) {
        this.$toast.error("Company identifier cannot be empty.", "Error", {
          position: "topCenter"
        });
        return;
      }

      this.time = new Date();
      this.timer = setInterval(() => {
        if (this.time) {
          this.offset = Number(
            (new Date().getTime() - this.time.getTime()) / 1000
          );
        }
      }, 1000);

      await this.setupServerStream(
        company_no,
        res => {
          try {
            let { type, data, message } = res;
            if (res.type && res.type === "done") {
              this.$swal(
                "Success",
                company_no + " server started successfully."
              );
              this.$emit("started", this.company);
              clearInterval(this.timer);
              this.timer = null;
              this.button = "Close";
              return;
            }
            if (!this.response[type]) {
              this.response[type] = [];
            }
            this.response[type].unshift(data || message);
          } catch (error) {
            this.response["error"].unshift(
              error.message || error.toString()
            );
          }
        },
        error => {
          this.response["error"].unshift(
            error.message || error.toString()
          );
        }
      );
    }
  }
};
</script>

<style scoped lang="css">
.setup-log-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px;
}

.modal-header__left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.modal-header__icon-wrap {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1e293b, #0f172a);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-header__icon {
  color: #64B5F6 !important;
  font-size: 20px !important;
}

.modal-header__title {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.2px;
}

.modal-header__sub {
  font-family: 'SF Mono', 'Roboto Mono', monospace;
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
  background: #f1f5f9;
  padding: 1px 6px;
  border-radius: 4px;
  display: inline-block;
}

.modal-close-btn {
  color: #94a3b8 !important;
}

.modal-divider {
  border-color: #f1f5f9 !important;
}

/* Log tabs */
.log-tabs {
  display: flex;
  gap: 4px;
  padding: 12px 16px 0;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.log-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  background: transparent;
  border-radius: 8px 8px 0 0;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.15s ease;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
}

.log-tab:hover {
  color: #475569;
  background: rgba(0,0,0,0.03);
}

.log-tab--active {
  background: #ffffff;
  border-bottom: 2px solid currentColor;
}

.log-tab--active.log-tab--info { color: #0288D1; }
.log-tab--active.log-tab--cmd  { color: #7C3AED; }
.log-tab--active.log-tab--error { color: #DC2626; }

.log-tab__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.log-tab__dot--info  { background: #0288D1; }
.log-tab__dot--cmd   { background: #7C3AED; }
.log-tab__dot--error { background: #DC2626; }

.log-tab__count {
  background: #f1f5f9;
  color: #64748b;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 11px;
}

/* Log output */
.log-output {
  height: 360px;
  overflow-y: auto;
  background: #0f172a;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: 'SF Mono', 'Roboto Mono', monospace;
}

.log-line {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.6;
}

.log-line--info  { color: #86efac; }
.log-line--error { color: #fca5a5; background: rgba(220,38,38,0.08); }
.log-line--cmd   { color: #c4b5fd; }

.log-line__prefix {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  opacity: 0.55;
  letter-spacing: 0.5px;
  padding-top: 2px;
  min-width: 32px;
}

.log-line__text {
  word-break: break-all;
  flex: 1;
}

.log-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;
  color: rgba(255,255,255,0.3);
}

.log-empty__icon {
  font-size: 40px !important;
  color: rgba(255,255,255,0.2) !important;
}

.log-empty__text {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 13px;
  text-align: center;
  margin: 0;
}

/* Footer */
.modal-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
}

.log-timer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: rgba(251,140,0,0.1);
  border-radius: 20px;
}

.log-timer__icon {
  color: #FB8C00 !important;
}

.log-timer__text {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #FB8C00;
}

.modal-cancel-btn {
  font-family: 'Inter', 'Roboto', sans-serif !important;
  font-weight: 500 !important;
  text-transform: none !important;
  color: #64748b !important;
  border-radius: 10px !important;
}

.modal-action-btn {
  font-family: 'Inter', 'Roboto', sans-serif !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 10px !important;
  height: 40px !important;
  padding: 0 20px !important;
}
</style>
